import React from 'react';
import { FiMoreVertical, FiClock, FiSmartphone, FiMapPin, FiEye, FiBriefcase, FiEdit } from 'react-icons/fi';
import { Card } from '../../components';
import './style.css';
import './AnomalyCard.css';

// Helper for distance calculation (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const d = R * c; // Distance in km
    return d;
};

const deg2rad = (deg) => {
    return deg * (Math.PI/180);
};

const parseTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
};

export default function AnomalyCard({ 
  title, 
  icon: Icon, 
  color, 
  type, 
  data = [],
  onViewPoint,
  onEditPoint
}) {

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getRelevantRecord = (records, type) => {
    if (!records || records.length === 0) return null;
    if (type === 'early_exit') return records[records.length - 1];
    if (type === 'interval_delay' && records.length >= 3) return records[2];
    if (type === 'outside_fence') return records.find(r => r.is_outside_fence || r.is_outside_fence_client) || records[0];
    return records[0];
  };

  return (
    <Card className="tko-anomaly-card">
      <div className="tko-anomaly-card-header">
        <h3 className="tko-anomaly-card-title">
          {Icon && <Icon size={20} color={color} />}
          {title}
        </h3>
        <span 
            className="tko-anomaly-count-badge"
            style={{ backgroundColor: color || '#3b82f6' }}
        >
            {data.length}
        </span>
      </div>
      
      <div className="tko-anomaly-table-wrapper">
        <table className="tko-anomaly-table">
          <thead>
            <tr>
              <th>Detalhes</th>
              <th>Competência</th>
              <th>Local</th>
              <th>Colaborador</th>
              <th>Ponto</th>
              <th>Previsto</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
                <tr><td colSpan="6" className="tko-anomaly-empty">Nenhum registro encontrado</td></tr>
            ) : (
                data.map((item, idx) => {
                    const relevantRecord = getRelevantRecord(item.records, type);
                    const photoSrc = relevantRecord?.photo;
                    
                    return (
                    <tr key={idx}>
                        <td className="tko-anomaly-action-cell">
                             <button 
                                className="tko-anomaly-action-btn" 
                                onClick={() => onViewPoint && onViewPoint(item)}
                                title="Ver Detalhes"
                                style={{borderRadius: '8px'}}
                             >
                                <FiEye size={16} />
                                <b style={{fontSize: '10pt'}}>&nbsp;VER</b>
                             </button>
                        </td>
                        <td className="tko-anomaly-date" style={{ color: color }}>
                            {formatDate(item.competence_date)}
                        </td>
                        <td>{item.records[0]?.station_name || 'N/A'}</td>
                        <td>
                            <div className="tko-anomaly-employee">
                                <div className="tko-anomaly-avatar">
                                    {photoSrc ? (
                                        <img 
                                            src={photoSrc.startsWith('data:') ? photoSrc : `data:image/jpeg;base64,${photoSrc}`} 
                                            alt="Ponto" 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                        />
                                    ) : (
                                        item.employee_name.charAt(0)
                                    )}
                                </div>
                                <div className="tko-anomaly-employee-info">
                                    <div className="tko-anomaly-employee-name">{item.employee_name}</div>
                                    <div className="tko-anomaly-meta">
                                        <FiBriefcase size={12} />
                                        <span className="tko-anomaly-meta-text" title={`${item.registration_number} | ${item.position_name}`}>
                                            {item.registration_number} | {item.position_name}
                                        </span>
                                    </div>
                                    <div className="tko-anomaly-meta">
                                        <span className="tko-anomaly-meta-text">{item.journey_name}</span>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div className="tko-anomaly-time-stack">
                                {item.records.map((r, i) => {
                                    // Highlight logic:
                                    // entry_delay: highlight first record
                                    // early_exit: highlight last record
                                    // interval_delay: complex, but usually middle records. 
                                    // For now simplified logic as per previous implementation
                                    const isAnomaly = (type === 'entry_delay' && i === 0) || 
                                                      (type === 'early_exit' && i === item.records.length - 1);
                                    
                                    return (
                                        <span key={i} className={`tko-anomaly-time ${isAnomaly ? 'highlight' : ''}`}
                                            style={isAnomaly ? { color: color, backgroundColor: `${color}15` } : {}}
                                        >
                                            {r.time}h
                                        </span>
                                    );
                                })}
                            </div>
                        </td>
                        <td>
                            <div className="tko-anomaly-time-stack">
                                <span className="tko-anomaly-time">{item.journey?.start_time ? `${item.journey.start_time.substring(0,5)}h` : '-'}</span>
                                {item.journey?.end_time && <span className="tko-anomaly-time">{item.journey.end_time.substring(0,5)}h</span>}
                            </div>
                        </td>
                    </tr>
                    )})
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
